<template>
  <div class="border border-border-secondary rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-200">
    <div class="flex flex-col gap-4 p-4 items-start">
      <div class="min-w-0">
        <div class="flex items-start gap-2">
          <div class="text-xl font-bold text-primary line-clamp-2 flex-1">
            {{ offer.title }}
          </div>
          <Button
            v-if="canManageVehicleOffers"
            icon="gear"
            plain
            severity="secondary"
            size="sm"
            alt="Manage vehicle offers"
            router-link="/settings/vehicles/offers"
          />
        </div>
        <div class="text-sm text-tertiary mt-1">
          {{ formattedDateRange }}
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <Chip
          size="sm"
          :rounded="false"
          outlined
        >
          {{ formattedCondition }}
        </Chip>
        <Chip
          v-if="offer.isArchived"
          size="sm"
          :rounded="false"
          severity="secondary"
        >
          Archived
        </Chip>
        <Chip
          v-if="expirationChipLabel"
          size="sm"
          :rounded="false"
          severity="danger"
        >
          {{ expirationChipLabel }}
        </Chip>
        <Chip
          v-for="vehicleLabel in targetVehiclePreview"
          :key="vehicleLabel"
          size="sm"
          :rounded="false"
        >
          {{ vehicleLabel }}
        </Chip>
        <Chip
          v-if="remainingTargetVehicleCount > 0"
          size="sm"
          :rounded="false"
          severity="secondary"
        >
          +{{ remainingTargetVehicleCount }} more
        </Chip>
      </div>

      <div class="rounded-lg border border-primary p-3 text-sm">
        <div class="text-tertiary uppercase tracking-wide text-xs">Deposit Contribution</div>
        <div class="text-lg font-semibold mt-1">{{ formattedDepositContribution }}</div>
      </div>

      <div class="flex flex-col gap-2">
        <div class="text-tertiary uppercase tracking-wide text-xs">Dealerships</div>
        <div class="flex flex-wrap gap-2">
          <Chip
            v-for="dealershipLabel in dealershipLabels"
            :key="dealershipLabel"
            size="sm"
            :rounded="false"
          >
            {{ dealershipLabel }}
          </Chip>
          <Chip
            v-if="dealershipLabels.length === 0"
            size="sm"
            :rounded="false"
            severity="secondary"
          >
            No dealerships
          </Chip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// libraries
import { computed } from 'vue'
import dayjs from 'dayjs'
import { useAbility } from '@casl/vue'

// components
import Button from '@/components/button/Button.vue'
import Chip from '@/components/chip/Chip.vue'

// types
import type { VehicleOffer } from '@/types/domain/vehicleOffer'

// utilities
import { formatCurrency } from '@/utilities/number/formatNumber'

const props = defineProps<{
  offer: VehicleOffer
}>()

const { can } = useAbility()
const canCheck = (permission: string) => (can as (action: string, subject?: unknown, field?: string) => boolean)(permission)

/**
 * Returns true when the current user can manage vehicle offers.
 */
const canManageVehicleOffers = computed<boolean>(() => {
  return canCheck('manage_vehicle_offers')
})

/**
 * Formats the offer condition into a user-facing label.
 */
const formattedCondition = computed<string>(() => {
  if (props.offer.vehicleCondition === 'new') {
    return 'New'
  }

  if (props.offer.vehicleCondition === 'used') {
    return 'Used'
  }

  return 'All Vehicles'
})

/**
 * Formats the deposit contribution using the shared number formatter.
 */
const formattedDepositContribution = computed<string>(() => {
  return formatCurrency(props.offer.depositContribution, 'GBP', 0) || 'GBP 0'
})

/**
 * Formats the offer start and end dates for the card.
 */
const formattedDateRange = computed<string>(() => {
  const startDate = dayjs(props.offer.startDate)
  const endDate = dayjs(props.offer.endDate)

  if (!startDate.isValid() || !endDate.isValid()) {
    return 'Date unavailable'
  }

  return `${startDate.format('DD MMM YYYY')} - ${endDate.format('DD MMM YYYY')}`
})

/**
 * Returns the expiration chip label when an offer expires within the next two days.
 */
const expirationChipLabel = computed<string | null>(() => {
  const endDate = dayjs(props.offer.endDate)
  const today = dayjs().startOf('day')

  if (!endDate.isValid()) {
    return null
  }

  const daysUntilExpiry = endDate.startOf('day').diff(today, 'day')

  if (daysUntilExpiry === 0) {
    return 'Expires today'
  }

  if (daysUntilExpiry > 0 && daysUntilExpiry <= 2) {
    return `Expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}`
  }

  return null
})

/**
 * Returns a short preview of the targeted vehicles.
 */
const targetVehiclePreview = computed<string[]>(() => {
  return props.offer.targetVehicles
    .map(vehicle => {
      return [vehicle.make, vehicle.modelMarketingName || vehicle.model].filter(Boolean).join(' ')
    })
    .filter(vehicleLabel => vehicleLabel.length > 0)
    .slice(0, 2)
})

/**
 * Returns the number of hidden targeted vehicles beyond the preview.
 */
const remainingTargetVehicleCount = computed<number>(() => {
  return Math.max(props.offer.targetVehicles.length - targetVehiclePreview.value.length, 0)
})

/**
 * Returns dealership labels for the card chip list.
 */
const dealershipLabels = computed<string[]>(() => {
  return props.offer.dealerships
    .map(dealership => dealership.displayName || `Dealership #${dealership.id}`)
    .filter(label => label.length > 0)
})
</script>

<style scoped></style>
